/**
 * Frontend-facing messages for the stable Google error codes returned by the
 * server (Phase 2). The UI keys off these codes — never message text — so a
 * Classroom failure always shows an actionable next step.
 *
 * Keep in sync with `server/lib/googleErrors.js`.
 */

export const GOOGLE_RECONNECT_REQUIRED = 'GOOGLE_RECONNECT_REQUIRED';
export const GOOGLE_SCOPE_MISSING = 'GOOGLE_SCOPE_MISSING';
export const GOOGLE_RATE_LIMITED = 'GOOGLE_RATE_LIMITED';
export const GOOGLE_TEMPORARY_FAILURE = 'GOOGLE_TEMPORARY_FAILURE';
export const GOOGLE_PERMISSION_DENIED = 'GOOGLE_PERMISSION_DENIED';
export const GOOGLE_TIMEOUT = 'GOOGLE_TIMEOUT';
export const GOOGLE_UNKNOWN_ERROR = 'GOOGLE_UNKNOWN_ERROR';

export const GOOGLE_ERROR_MESSAGES: Record<string, string> = {
  [GOOGLE_RECONNECT_REQUIRED]:
    'Your Google Classroom connection expired. Reconnect Google to continue. Your work is saved.',
  [GOOGLE_SCOPE_MISSING]:
    'DoneGrading needs additional Google Classroom permission to sync this data. Reconnect Google and approve the requested access.',
  [GOOGLE_RATE_LIMITED]:
    'Google Classroom is temporarily rate-limiting requests. Wait a minute and try again.',
  [GOOGLE_TEMPORARY_FAILURE]: 'Google Classroom is temporarily unavailable. Try again in a minute.',
  [GOOGLE_PERMISSION_DENIED]: 'Your Google account or school may not allow this Classroom action.',
  [GOOGLE_TIMEOUT]: 'Google Classroom took too long to respond. Try again. Your work is saved.',
  [GOOGLE_UNKNOWN_ERROR]: 'Could not sync with Google Classroom. Try again.',
};

/** Friendly message for a stable Google error code, or null if not one. */
export function messageForGoogleErrorCode(code: string | null | undefined): string | null {
  if (!code) return null;
  return GOOGLE_ERROR_MESSAGES[code] ?? null;
}

/**
 * Best-effort extraction of a structured error code from an unknown error.
 * Handles ScanApiError-shaped objects ({ body: { code } }) and plain
 * code-bearing errors ({ code }).
 */
export function extractGoogleErrorCode(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null;
  const anyErr = err as {
    code?: unknown;
    body?: { code?: unknown; googleErrorCode?: unknown };
  };
  // The server attaches `googleErrorCode` as the authoritative stable code
  // (without disturbing the historical `code`), so prefer it.
  const fromGoogle = anyErr.body?.googleErrorCode;
  if (typeof fromGoogle === 'string' && fromGoogle in GOOGLE_ERROR_MESSAGES) return fromGoogle;
  const fromBody = anyErr.body?.code;
  if (typeof fromBody === 'string' && fromBody in GOOGLE_ERROR_MESSAGES) return fromBody;
  const fromTop = anyErr.code;
  if (typeof fromTop === 'string' && fromTop in GOOGLE_ERROR_MESSAGES) return fromTop;
  return null;
}
